import { useCallback, useEffect, useMemo, useRef } from "react";
import cx from "classnames";
import { Flex, Spinner, Text } from "@radix-ui/themes";
import Select, { SelectOption } from "@/components/shared/select";
import { QuackChat } from "@/components/quack-chat/QuackChat";
import { Message } from "@/components/quack-chat/core/entities/Message";
import { useQuackchatPlatformConfiguration } from "@/api/quackchat/configuration/useQuackchatPlatformConfiguration";
import { useQuackchatSessionConfiguration } from "@/api/quackchat/configuration/useQuackchatSessionConfiguration";
import { AutomationConfigurationContentHeader } from "../parts/AutomationConfigurationContentHeader";
import { FieldGroup, OptionalFieldGroup } from "../parts/FieldGroup";
import styles from "../AutomationConfiguration.module.scss";
import {
  ChatSettings,
  CsatType,
} from "@/components/quack-chat/QuackChatConfig.model";
import { mergeStatus } from "@/api/quackchat/configuration/useConfigManager";
import Checkbox from "@/components/shared/inputs/checkbox";
import { InlineTextField } from "@/components/shared/inputs/inilie-text-field/InlineTextField";
import { useGetQuackChatSystemMessage } from "./system-message/useGetQuackChatSystemMessage";
function isValidCsatType(value: string): value is CsatType {
  return value === "rating" || value === "binary";
}

const timeOptions: SelectOption[] = [
  ...[...Array(10)].map((_, i) => ({
    label: `${i + 1} minute${i > 0 ? "s" : ""}`,
    value: `${(i + 1) * 60}`,
  })),
  ...[15, 20, 25, 30].map((minutes) => ({
    label: `${minutes} minutes`,
    value: `${minutes * 60}`,
  })),
  { label: "1 hour", value: `${60 * 60}` },
  { label: "2 hours", value: `${2 * 60 * 60}` },
];

const followUpCountOptions: SelectOption[] = [1, 2, 3].map((n) => ({
  label: `${n} time${n > 1 ? "s" : ""}`,
  value: `${n}`,
}));

const csatTypeOptions: SelectOption[] = [
  { label: "Rating", value: "rating" },
  { label: "Binary", value: "binary" },
];

export function QuackchatEndOfConversationConfiguration({
  simulatorId,
}: {
  simulatorId: string | undefined;
}) {
  const platformConfiguration = useQuackchatPlatformConfiguration(simulatorId);

  const sessionConfiguration = useQuackchatSessionConfiguration(simulatorId);

  const { data: followupMessage } = useGetQuackChatSystemMessage(
    "FOLLOW_UP_REMINDER",
    simulatorId
  );

  const { data: closeChatMessage } = useGetQuackChatSystemMessage(
    "CLOSED_CHAT",
    simulatorId
  );

  const { data: liveAgentBusyMessage } = useGetQuackChatSystemMessage(
    "LIVE_AGENT_BUSY_REMINDER",
    simulatorId
  );

  const messages = useMemo<Message[]>(() => {
    const messages: Message[] = [];

    if (
      followupMessage != null &&
      followupMessage.enabled === true &&
      sessionConfiguration.data?.followUpReminderAfterSeconds &&
      sessionConfiguration.data?.followUpReminderAfterSeconds > 0
    ) {
      messages.push({
        action: "standard_message",
        author: "AUTO_AGENT",
        text: followupMessage?.message ?? "",
        deduplicationKey: "FOLLOW_UP_REMINDER",
        createdAt: "2021-09-01T12:00:00Z",
        data: {},
      });
    }

    if (
      liveAgentBusyMessage != null &&
      liveAgentBusyMessage.enabled === true &&
      sessionConfiguration.data?.liveAgentBusyTimeoutAfterSeconds &&
      sessionConfiguration.data?.liveAgentBusyTimeoutAfterSeconds > 0
    ) {
      messages.push({
        action: "standard_message",
        author: "AUTO_AGENT",
        text: liveAgentBusyMessage?.message ?? "",
        deduplicationKey: "LIVE_AGENT_BUSY_REMINDER",
        createdAt: "2021-09-01T12:02:00Z",
        data: {},
      });
    }

    if (closeChatMessage != null && closeChatMessage.enabled === true) {
      messages.push({
        action: "standard_message",
        author: "AUTO_AGENT",
        text: closeChatMessage?.message ?? "",
        deduplicationKey: "CLOSED_CHAT",
        createdAt: "2021-09-01T12:10:00Z",
        data: {},
      });
    }

    return messages;
  }, [
    followupMessage,
    closeChatMessage,
    liveAgentBusyMessage,
    sessionConfiguration.data?.followUpReminderAfterSeconds,
    sessionConfiguration.data?.liveAgentBusyTimeoutAfterSeconds,
  ]);

  const foramttedErrors = useMemo(() => {
    if (sessionConfiguration.errors == null) {
      return;
    }

    return Object.fromEntries(
      Object.entries(sessionConfiguration.errors).map(([key, value]) => {
        return [
          key,
          value
            .replaceAll(
              "checkInactivityTimeoutAfterSeconds",
              "Inactivity Timeout"
            )
            .replaceAll("followUpReminderAfterSeconds", "Follow-up Reminder")
            .replaceAll(
              "liveAgentBusyTimeoutAfterSeconds",
              "Live Agent Busy Timeout"
            )
            .toLowerCase(),
        ];
      })
    );
  }, [sessionConfiguration.errors]);

  const updateStatus = useMemo(() => {
    return mergeStatus(
      sessionConfiguration.updateStatus,
      platformConfiguration.updateStatus
    );
  }, [sessionConfiguration.updateStatus, platformConfiguration.updateStatus]);

  const platformConfigurationRef = useRef(platformConfiguration.data);
  const updateChatSettings = useCallback(
    (chatSettings: Partial<ChatSettings>) => {
      if (platformConfigurationRef.current == null) {
        return;
      }

      platformConfiguration.update({
        ...platformConfigurationRef.current,
        chatSettings: {
          ...platformConfigurationRef.current.chatSettings,
          ...chatSettings,
        },
      });
    },
    [platformConfiguration.update]
  );

  useEffect(() => {
    platformConfigurationRef.current = platformConfiguration.data;
  }, [platformConfiguration.data]);

  if (
    sessionConfiguration.isLoading ||
    sessionConfiguration.data == null ||
    platformConfiguration.isLoading ||
    platformConfiguration.data == null
  ) {
    return <Spinner />;
  }

  return (
    <Flex gap={"24px"}>
      <Flex flexGrow={"1"} direction={"column"} gap={"24px"}>
        <AutomationConfigurationContentHeader
          title={"End of Conversation"}
          status={updateStatus}
        />

        <Flex direction={"column"}>
          <OptionalFieldGroup
            label={"Collect Customer Feedback"}
            description={"Send a CSAT survey when the conversation ends."}
            enabled={sessionConfiguration.data.showCsat}
            toggleEnabled={(nextValue) =>
              sessionConfiguration.update({ showCsat: nextValue })
            }
            error={foramttedErrors?.showCsat}
          >
            <Flex width={"100%"} direction={"column"}>
              <FieldGroup horizontal description={"CSAT Type"}>
                <Select
                  value={platformConfiguration.data.chatSettings.csatType}
                  onChange={(newValue) => {
                    if (isValidCsatType(newValue)) {
                      updateChatSettings({
                        csatType: newValue,
                      });
                    }
                  }}
                  options={csatTypeOptions}
                  classes={{
                    trigger: cx(styles.Select, styles.small),
                    value: styles.SelectValue,
                  }}
                  disabled={sessionConfiguration.data.showCsat === false}
                />
              </FieldGroup>

              <FieldGroup horizontal description={"CSAT Comment"}>
                <Checkbox
                  label="Allow comment"
                  value={
                    platformConfiguration.data.chatSettings.allowCsatComment ??
                    false
                  }
                  onChange={(checked) =>
                    updateChatSettings({
                      allowCsatComment: checked,
                    })
                  }
                  disabled={sessionConfiguration.data.showCsat === false}
                />
              </FieldGroup>

              <FieldGroup horizontal description={"CSAT Comment Text"}>
                <InlineTextField
                  value={
                    platformConfiguration.data.chatSettings.csatCommentText
                  }
                  onChange={(newValue) =>
                    updateChatSettings({
                      csatCommentText: newValue,
                    })
                  }
                  placeholder={"Help us improve your experience"}
                  disabled={
                    sessionConfiguration.data.showCsat === false ||
                    platformConfiguration.data.chatSettings.allowCsatComment ===
                      false
                  }
                />
              </FieldGroup>
            </Flex>
          </OptionalFieldGroup>

          <FieldGroup
            label={"Inactivity Timeout"}
            description={
              "If a user doesn't respond within the set time, they will be marked inactive, and the chat will close. Users can always restart a new conversation."
            }
            error={foramttedErrors?.checkInactivityTimeoutAfterSeconds}
          >
            <Flex width={"100%"} align={"center"} gap={"2"}>
              <Text
                size={"2"}
                color={"gray"}
                style={{ flexGrow: 1, maxWidth: 260 }}
              >
                End conversation after
              </Text>

              <Select
                value={sessionConfiguration.data.checkInactivityTimeoutAfterSeconds?.toString()}
                onChange={(value) => {
                  sessionConfiguration.update({
                    checkInactivityTimeoutAfterSeconds: parseInt(value),
                  });
                }}
                options={timeOptions}
                classes={{
                  trigger: cx(styles.Select, styles.small),
                  value: styles.SelectValue,
                }}
              />
            </Flex>
          </FieldGroup>

          <OptionalFieldGroup
            label={"Follow-up Reminder"}
            description={
              "Send a message if the user becomes inactive to check if they still need assistance."
            }
            enabled={sessionConfiguration.data.followUpReminderAfterSeconds > 0}
            toggleEnabled={(nextValue) =>
              sessionConfiguration.update({
                followUpReminderAfterSeconds: nextValue ? 60 : 0,
              })
            }
            error={foramttedErrors?.followUpReminderAfterSeconds}
          >
            <Flex width={"100%"} direction={"column"} gap={"2"}>
              <Flex width={"100%"} align={"center"} gap={"2"}>
                <Text
                  size={"2"}
                  color={"gray"}
                  style={{ flexGrow: 1, maxWidth: 260 }}
                >
                  Send after
                </Text>
                <Select
                  value={sessionConfiguration.data.followUpReminderAfterSeconds?.toString()}
                  onChange={(value) => {
                    sessionConfiguration.update({
                      followUpReminderAfterSeconds: parseInt(value),
                    });
                  }}
                  options={timeOptions}
                  classes={{
                    trigger: cx(styles.Select, styles.small),
                    value: styles.SelectValue,
                  }}
                />
              </Flex>
              <Flex width={"100%"} align={"center"} gap={"2"}>
                <Text
                  size={"2"}
                  color={"gray"}
                  style={{ flexGrow: 1, maxWidth: 260 }}
                >
                  Send up to
                </Text>
                <Select
                  value={sessionConfiguration.data.followUpCount?.toString()}
                  onChange={(value) => {
                    sessionConfiguration.update({
                      followUpCount: parseInt(value),
                    });
                  }}
                  options={followUpCountOptions}
                  classes={{
                    trigger: cx(styles.Select, styles.small),
                    value: styles.SelectValue,
                  }}
                />
              </Flex>
            </Flex>
          </OptionalFieldGroup>

          <OptionalFieldGroup
            label={"Live Agent Busy Timeout"}
            description={
              "Send a message if the user becomes inactive to check if they still need assistance."
            }
            enabled={
              sessionConfiguration.data.liveAgentBusyTimeoutAfterSeconds > 0
            }
            toggleEnabled={(nextValue) =>
              sessionConfiguration.update({
                liveAgentBusyTimeoutAfterSeconds: nextValue ? 60 : 0,
              })
            }
            error={foramttedErrors?.liveAgentBusyTimeoutAfterSeconds}
          >
            <Flex width={"100%"} align={"center"} gap={"2"}>
              <Text
                size={"2"}
                color={"gray"}
                style={{ flexGrow: 1, maxWidth: 260 }}
              >
                Send after
              </Text>
              <Select
                value={sessionConfiguration.data.liveAgentBusyTimeoutAfterSeconds?.toString()}
                onChange={(value) => {
                  sessionConfiguration.update({
                    liveAgentBusyTimeoutAfterSeconds: parseInt(value),
                  });
                }}
                options={timeOptions}
                classes={{
                  trigger: cx(styles.Select, styles.small),
                  value: styles.SelectValue,
                }}
              />
            </Flex>
          </OptionalFieldGroup>

          <OptionalFieldGroup
            label={"Auto-confirm Escalation Email"}
            description={
              "If the user's email is already identified, auto-confirm the escalation email."
            }
            enabled={
              sessionConfiguration.data.autoFillEscalationEmailIfEmailExists
            }
            toggleEnabled={(nextValue) =>
              sessionConfiguration.update({
                autoFillEscalationEmailIfEmailExists: nextValue,
              })
            }
            error={foramttedErrors?.autoFillEscalationEmailIfEmailExists}
          />
        </Flex>
      </Flex>

      <Flex flexShrink={"0"}>
        <QuackChat
          config={platformConfiguration.data}
          messages={messages}
          showCSAT={sessionConfiguration.data.showCsat}
        />
      </Flex>
    </Flex>
  );
}
